// src/types/Product.ts
// NOTE: "Product" below represents a single CAR listed for sale (the vehicle
// inventory record). Kept as `Product` internally for compatibility with the
// existing data layer, but every field below is car-domain.
export const FUEL_TYPES = ['Petrol', 'Diesel', 'Hybrid', 'Electric', 'LPG/Gas'] as const;
export const TRANSMISSION_TYPES = ['Automatic', 'Manual', 'CVT', 'Semi-Automatic'] as const;
export const CAR_CONDITIONS = ['Brand New', 'Used - Excellent', 'Used - Good', 'Used - Fair', 'Reconditioned', 'Salvage'] as const;
export const DRIVE_TYPES = ['FWD', 'RWD', 'AWD', '4WD'] as const;
export const CAR_STATUSES = ['In Stock', 'Reserved', 'Sold', 'Delivered', 'In Transit (Import)'] as const;

export type FuelType = typeof FUEL_TYPES[number];
export type TransmissionType = typeof TRANSMISSION_TYPES[number];
export type CarCondition = typeof CAR_CONDITIONS[number];
export type DriveType = typeof DRIVE_TYPES[number];
export type CarStatus = typeof CAR_STATUSES[number];

export interface Product {
  id?: string;
  name: string; // e.g. "2021 Toyota Aqua G Grade"
  description: string;
  price: number; // asking/selling price
  discount?: number; // Discount percentage (0-100)
  unitCost?: number; // landed/purchase cost (used for profit reports)
  categoryId: string; // body type: Sedan, SUV, Hatchback, etc.
  subcategoryId?: string;
  brandId?: string; // car brand/make (Toyota, Honda, BMW...)
  modelId?: string; // car model (Corolla, Civic...) — see enums/carModel.ts
  trimId?: string; // specific trim/spec within the model (GLi, Altis...)
  trimName?: string; // denormalized trim name, for display without a join
  sku: string; // Chassis / VIN number — unique per physical vehicle
  images: string[];
  // Featured listing flag (shown on client homepage)
  isFeatured: boolean;
  // Whether this car shows up on the client-facing site at all. Defaults to
  // true — staff explicitly hide a listing (e.g. to pull it while details
  // are being corrected) rather than opting in to visibility.
  isVisible?: boolean;
  // A car is exactly one physical unit at exactly one branch — no stock
  // quantity, no multi-warehouse allocation. Availability is driven entirely
  // by `status` below (see CarStatus).
  branchId?: string;
  branchName?: string;
  // ── Car-specific specification fields ─────────────────────────────
  modelYear?: number;
  registrationYear?: number;
  mileageKm?: number;
  fuelType?: FuelType;
  transmission?: TransmissionType;
  engineCapacityCc?: number;
  driveType?: DriveType;
  seatingCapacity?: number;
  exteriorColor?: string;
  interiorColor?: string;
  condition?: CarCondition;
  status?: CarStatus;
  registrationNumber?: string; // license plate, if registered locally
  ownersCount?: number; // number of previous owners
  isImported?: boolean; // true if this unit originated from an Import Order
  importOrderId?: string; // link back to the Import Order that brought this car in
  countryOfOrigin?: string;
  features?: string[]; // e.g. ["Sunroof", "Reverse Camera", "Alloy Wheels"]
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  // ── Sale details — set only when this car is marked Sold from the Sold
  // Vehicles page (see admin/src/admin/pages/sold-vehicles). Covers both
  // reservation-backed sales and direct walk-in/pre-owned sales, so every
  // sold car has buyer/payment info regardless of how the sale started.
  buyerName?: string;
  buyerPhone?: string;
  buyerEmail?: string;
  buyerIdNumber?: string; // NIC / driving licence / passport no. used to verify the buyer
  buyerAddress?: string;
  finalSalePrice?: number;
  depositAmount?: number;
  paymentMethod?: string;
  deliveryMethod?: string;
  deliveryDate?: string; // ISO date the vehicle is/was handed over
  soldByUserId?: string;
  soldByName?: string;
  soldAt?: Date;
  reservationId?: string;
  saleNotes?: string;
  // The employee (salesperson) credited with this sale — distinct from
  // soldByUserId/soldByName, which is the admin user who filled in the form.
  salespersonId?: string;
  salespersonName?: string;
  // ── Trade-in applied as part payment (separate from converting the traded
  // vehicle itself into new inventory — see enums/tradeIn.ts convertedToCarId) ─
  tradeInId?: string;
  tradeInValue?: number;
  // ── Financing — only meaningful when paymentMethod === 'Financing' ────────
  financingProvider?: string;
  financingTermMonths?: number;
  monthlyInstallment?: number;
  // ── Registration transfer & warranty ───────────────────────────────────────
  registrationTransferStatus?: RegistrationTransferStatus;
  warrantyMonths?: number;
  warrantyNotes?: string;
  // Related documents attached from the Sold Vehicles operation drawer
  // (title deed, invoice, ID copy, warranty card, etc.)
  documents?: SaleDocument[];
}

export const REGISTRATION_TRANSFER_STATUSES = ['Not Started', 'In Progress', 'Completed', 'Not Applicable'] as const;
export type RegistrationTransferStatus = typeof REGISTRATION_TRANSFER_STATUSES[number];

// A file (title deed, invoice, ID copy, warranty card, etc.) attached to a
// sold vehicle from the Sold Vehicles operation drawer.
export interface SaleDocument {
  name: string;
  url: string;
  uploadedAt?: string; // ISO date string
}

export interface ProductFormData {
  name: string;
  description: string;
  price: string;
  discount: string; // Discount percentage as string for form input
  unitCost: string;
  isFeatured?: boolean;
  isVisible?: boolean;
  categoryId: string;
  subcategoryId: string;
  brandId: string; // Optional brand association (empty string = none)
  modelId: string;
  trimId: string;
  sku: string;
  images: File[];
  // One car, one branch — no stock-quantity fields.
  branchId: string;
  // ── Car specification fields (form/string versions) ─────────────────
  modelYear: string;
  registrationYear: string;
  mileageKm: string;
  fuelType: string;
  transmission: string;
  engineCapacityCc: string;
  driveType: string;
  seatingCapacity: string;
  exteriorColor: string;
  interiorColor: string;
  condition: string;
  status: string;
  registrationNumber: string;
  ownersCount: string;
  isImported: boolean;
  importOrderId: string;
  countryOfOrigin: string;
  features: string[];
}

// Warehouse Allocation Interface (for form data)
export interface WarehouseAllocation {
  warehouseId: string;
  warehouseName?: string;
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
}

// Warehouse Inventory Record (stored separately)
export interface WarehouseInventory {
  id?: string;
  productId: string;
  productName?: string;
  variantId?: string;
  variantName?: string;
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  lastUpdated?: Date;
  sku?: string;
  price?: number;
  discount?: number; // Discount percentage
  // Weighted-average purchase cost for stock currently in this warehouse,
  // recomputed on every PO receipt. Always cost — never conflated with the
  // selling price above (see answers.text for the historical ambiguity here).
  avgCost?: number;
}

// Category Management Types
export interface Category {
  id?: string;
  name: string;
  description?: string;
  parentId?: string;
  imageUrl?: string | null;
  isActive: boolean;
  sortOrder?: number;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  isStarred?: boolean;
}

export interface CategoryFormData {
  name: string;
  description: string;
  parentId: string;
  isActive: boolean;
  sortOrder: string;
}

// Variant Management Types
export interface VariantOption {
  id?: string;
  name: string;
  value?: string;
  isActive: boolean;
}

export interface VariantType {
  id?: string;
  name: string;
  isRequired: boolean;
  allowsMultiple: boolean;
  options: VariantOption[];
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface VariantSelection {
  variantType: string;
  variantValue: string;
}

export interface ProductVariant {
  id?: string;
  productId?: string;
  variantSelections: VariantSelection[];
  price: number;
  discount?: number; // Discount percentage (0-100)
  unitCost?: number;
  sku: string;
  isDefault?: boolean;
  // Stock management for variants (total across all warehouses)
  stockQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  // Warehouse allocations (for UI only)
  warehouseAllocations?: WarehouseAllocation[];
}

export interface VariantTypeFormData {
  name: string;
  isActive: boolean;
  options: VariantOption[];
}

// Utility interfaces
export interface CategoryWithSubcategories extends Category {
  subcategories: Category[];
}

export interface ProductWithDetails extends Product {
  category?: Category;
  subcategory?: Category;
  variantDetails?: {
    variantSelections: VariantSelection[];
    price: number;
    discount?: number; // Discount percentage
    sku: string;
    isDefault?: boolean;
    stockQuantity: number;
    minStockLevel: number;
    maxStockLevel: number;
    warehouseAllocations?: WarehouseAllocation[];
    isLowStock: boolean;
    isOutOfStock: boolean;
  }[];
  warehouseInventories?: WarehouseInventory[];
}

// Global configuration for variants
export const VARIANT_CONFIG = {
  maxVariantSelections: 2,
} as const;

// Import Warehouse types
export interface Warehouse {
  id?: string;
  name: string;
  code: string;
  description: string;
  address: WarehouseAddress;
  contact: WarehouseContact;
  capacity: WarehouseCapacity;
  isActive: boolean;
  operatingHours?: WarehouseOperatingHours;
  // Whether this branch/show room shows up on the public client-facing
  // "Branches" page. Defaults to false — staff opt in a branch once its
  // details are confirmed correct, rather than every branch being public
  // by default.
  displayOnWebsite?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  createdByName?: string;
  updatedBy?: string;
  updatedByName?: string;
  // Statistics that will be updated
  stats?: WarehouseStats;
}

export interface WarehouseAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface WarehouseContact {
  phone: string;
  email: string;
  manager: string;
}

export interface WarehouseCapacity {
  total: number;
  used: number;
  available: number;
}

// Operating hours are set per-day so a branch can be open, say, Mon-Sat and
// closed Sunday rather than only having one flat open/close pair applied to
// every day of the week.
export const DAYS_OF_WEEK = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;
export type DayOfWeek = typeof DAYS_OF_WEEK[number];

export const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

export interface DayHours {
  open: string;
  close: string;
  closed: boolean;
}

export interface WarehouseOperatingHours {
  timezone: string;
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

export const createDefaultDayHours = (): DayHours => ({
  open: '09:00',
  close: '18:00',
  closed: false,
});

export const createDefaultOperatingHours = (): WarehouseOperatingHours => ({
  timezone: 'UTC',
  monday: createDefaultDayHours(),
  tuesday: createDefaultDayHours(),
  wednesday: createDefaultDayHours(),
  thursday: createDefaultDayHours(),
  friday: createDefaultDayHours(),
  saturday: createDefaultDayHours(),
  sunday: createDefaultDayHours(),
});

export interface WarehouseStats {
  productCount: number;
  variantCount: number;
  totalStock: number;
  lowStockCount: number;
  outOfStockCount: number;
}

export interface WarehouseFormData {
  name: string;
  code: string;
  description: string;
  address: WarehouseAddress;
  contact: WarehouseContact;
  capacity: {
    total: string;
    used: string;
  };
  isActive: boolean;
  operatingHours: WarehouseOperatingHours;
  displayOnWebsite: boolean;
}

export interface WarehouseDashboardStats {
  total: number;
  active: number;
  inactive: number;
  totalCapacity: number;
  usedCapacity: number;
  availableCapacity: number;
  utilizationRate: number;
}

// Warehouse with inventory
export interface WarehouseWithInventory extends Warehouse {
  inventory: WarehouseInventory[];
}

// Utility function to calculate discounted price
export const calculateDiscountedPrice = (price: number, discount?: number): number => {
  if (!discount || discount <= 0 || discount > 100) {
    return price;
  }
  return price - (price * discount / 100);
};

// Utility function to calculate discount amount
export const calculateDiscountAmount = (price: number, discount?: number): number => {
  if (!discount || discount <= 0 || discount > 100) {
    return 0;
  }
  return price * discount / 100;
};